import { useEffect, useRef, useState } from 'react'
import { Alert, App, Button, Col, DatePicker, Descriptions, Form, Input, Modal, Row, Select, Upload } from 'antd'
import type { UploadFile } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { fetchAccounts } from '@/features/finance/financeApi'
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  paySupplier,
  uploadSupplierAttachment,
  type PurchaseRow,
} from './supplierApi'

interface SupplierPaymentModalProps {
  purchase: PurchaseRow | null
  onClose: () => void
}

interface FormValues {
  amount: Money | null
  paidOn: Dayjs
  accountId: string
  reference?: string
  note?: string
}

export function SupplierPaymentModal({ purchase, onClose }: SupplierPaymentModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const [files, setFiles] = useState<UploadFile[]>([])
  const [failedFiles, setFailedFiles] = useState<string[]>([])
  const key = useRef('')

  useEffect(() => {
    if (!purchase) return
    key.current = newIdempotencyKey('supplier-payment')
    setFiles([])
    setFailedFiles([])
    form.setFieldsValue({ amount: purchase.remaining, paidOn: tenantToday(), reference: '', note: '' })
  }, [purchase, form])

  const accounts = useQuery({
    queryKey: queryKeys.finance.accounts(true),
    queryFn: ({ signal }) => fetchAccounts(true, signal),
    enabled: purchase !== null,
  })
  const accountOptions = (accounts.data ?? [])
    .filter((a) => a.type === 'ASSET' && a.postable && a.active && !a.requiresDimension)
    .map((a) => ({ value: a.id, label: `${a.code} · ${seededName('accounts', a.code, a.name, t)}` }))

  const pay = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!purchase) throw new Error('purchase')
      const payment = await paySupplier(
        purchase.id,
        {
          amountMinor: values.amount?.amount ?? 0,
          paidOn: values.paidOn.format('YYYY-MM-DD'),
          accountId: values.accountId,
          reference: values.reference?.trim() ? values.reference.trim() : null,
          note: values.note?.trim() ? values.note.trim() : null,
        },
        key.current,
      )
      const failed: string[] = []
      for (const file of files) {
        if (!file.originFileObj) continue
        try {
          await uploadSupplierAttachment(
            { owner: 'payment', supplierId: purchase.supplier.id, purchaseId: purchase.id, paymentId: payment.id },
            null,
            file.originFileObj,
          )
        } catch {
          failed.push(file.name)
        }
      }
      return failed
    },
    onSuccess: (failed) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.supplier.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      if (failed.length > 0) {
        setFailedFiles(failed)
        return
      }
      onClose()
    },
  })

  const close = () => {
    pay.reset()
    onClose()
  }

  return (
    <Modal
      open={purchase !== null}
      title={t('purchase.payment.title')}
      okText={t('purchase.payment.submit')}
      cancelText={t('common.cancel')}
      confirmLoading={pay.isPending}
      okButtonProps={{ disabled: failedFiles.length > 0 }}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => pay.mutate(values))}
      destroyOnHidden
      width={640}
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('purchase.payment.note')} />
      {pay.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(pay.error, t)} />
      ) : null}
      {failedFiles.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('purchase.payment.filesFailed', { files: failedFiles.join(', ') })}
        />
      ) : null}
      {purchase ? (
        <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('purchase.field.number')}>{purchase.number}</Descriptions.Item>
          <Descriptions.Item label={t('purchase.field.supplier')}>{purchase.supplier.name}</Descriptions.Item>
          <Descriptions.Item label={t('purchase.field.remaining')}>{formatMoneyValue(purchase.remaining)}</Descriptions.Item>
        </Descriptions>
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={pay.isPending || failedFiles.length > 0}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="amount"
              label={t('purchase.payment.amount')}
              rules={[
                {
                  validator: (_, value: Money | null) =>
                    value && value.amount > 0 && purchase && value.amount <= purchase.remaining.amount
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('purchase.validation.paymentAmount'))),
                },
              ]}
            >
              <MoneyInput currency={purchase?.currency} minMinor={1} maxMinor={purchase?.remaining.amount} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="paidOn" label={t('purchase.payment.paidOn')} rules={[{ required: true, message: t('purchase.validation.paidOn') }]}>
              <DatePicker style={{ width: '100%' }} disabledDate={(day) => day.isAfter(tenantToday(), 'day')} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="accountId"
              label={t('purchase.payment.account')}
              extra={t('purchase.payment.accountHint')}
              rules={[{ required: true, message: t('purchase.validation.paymentAccount') }]}
            >
              <Select showSearch optionFilterProp="label" options={accountOptions} loading={accounts.isPending} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="reference" label={t('purchase.payment.reference')}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="note" label={t('purchase.payment.noteField')}>
              <Input maxLength={500} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label={t('purchase.payment.proof')}>
              <Upload
                accept={ATTACHMENT_ACCEPT}
                fileList={files}
                multiple
                beforeUpload={(file) => {
                  if (file.size > ATTACHMENT_MAX_BYTES) {
                    void message.warning(t('supplier.attachments.tooLarge'))
                    return Upload.LIST_IGNORE
                  }
                  setFiles((current) => [...current, { uid: file.uid, name: file.name, originFileObj: file }])
                  return false
                }}
                onRemove={(file) => setFiles((current) => current.filter((f) => f.uid !== file.uid))}
              >
                <Button icon={<UploadOutlined />}>{t('purchase.payment.addProof')}</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
